import { supabase } from '../utils/supabase';

/**
 * Fetch partnership data for a specific player
 * @param {string} playerId - The player's ID
 * @param {string} clubId - The club ID to filter by
 * @param {string} seasonId - Optional season ID to filter by
 * @returns {Promise<Object>} Partnership overview data
 */
export const fetchPartnershipOverview = async (playerId, clubId, seasonId = null) => {
  try {
    // Get all partnerships where the player is either player1 or player2
    const { data: partnerships, error: partnershipsError } = await supabase
      .from('partnerships_with_stats')
      .select(`
        *,
        player1:player1_id(id, first_name, last_name, gender),
        player2:player2_id(id, first_name, last_name, gender)
      `)
      .or(`player1_id.eq.${playerId},player2_id.eq.${playerId}`);

    if (partnershipsError) throw partnershipsError;

    // Filter partnerships that have games in the specified club/season
    const filteredPartnerships = [];
    
    for (const partnership of partnerships) {
      // Get games for this partnership in the specified club/season
      const { data: partnershipGames, error: gamesError } = await supabase
        .from('partnership_games')
        .select(`
          session_game_id,
          session_games(
            id,
            club_id,
            season_id,
            created_at
          )
        `)
        .eq('partnership_id', partnership.id);

      if (gamesError) throw gamesError;

      // Filter by club and season if specified
      const relevantGames = partnershipGames.filter(pg => {
        const game = pg.session_games;
        return game && game.club_id === clubId && 
               (seasonId === null || game.season_id === seasonId);
      });

      // Only include partnerships that have games in the specified context
      if (relevantGames.length > 0) {
        filteredPartnerships.push({
          ...partnership,
          relevantGamesCount: relevantGames.length
        });
      }
    }

    // Calculate overview statistics
    const activePartnerships = filteredPartnerships.length;
    const avgChemistryScore = filteredPartnerships.length > 0 
      ? filteredPartnerships.reduce((sum, p) => sum + parseFloat(p.chemistry_score || 0) / 100, 0) / filteredPartnerships.length
      : 0;
    
    const totalWins = filteredPartnerships.reduce((sum, p) => sum + (p.total_wins || 0), 0);
    const totalGames = filteredPartnerships.reduce((sum, p) => sum + (p.total_games || 0), 0);
    const partnershipWinRate = totalGames > 0 ? (totalWins / totalGames) * 100 : 0;

    return {
      activePartnerships,
      avgChemistryScore: Math.round(avgChemistryScore * 100) / 100,
      partnershipWinRate: Math.round(partnershipWinRate * 100) / 100,
      partnerships: filteredPartnerships
    };
  } catch (error) {
    console.error('Error fetching partnership overview:', error);
    throw error;
  }
};

/**
 * Fetch top partnerships for a player
 * @param {string} playerId - The player's ID
 * @param {string} clubId - The club ID to filter by
 * @param {string} seasonId - Optional season ID to filter by
 * @param {number} limit - Number of top partnerships to return
 * @returns {Promise<Array>} Array of top partnerships
 */
export const fetchTopPartnerships = async (playerId, clubId, seasonId = null, limit = 5) => {
  try {
    const { data: partnerships, error } = await supabase
      .from('partnerships_with_stats')
      .select(`
        *,
        player1:player1_id(id, first_name, last_name, gender),
        player2:player2_id(id, first_name, last_name, gender)
      `)
      .or(`player1_id.eq.${playerId},player2_id.eq.${playerId}`)
      .order('chemistry_score', { ascending: false })
      .limit(limit);

    if (error) throw error;

    // Filter and enhance partnerships with relevant game data
    const enhancedPartnerships = [];
    
    for (const partnership of partnerships) {
      // Get partnership game stats for the specified club/season
      const { data: gameStats, error: statsError } = await supabase
        .from('partnership_game_stats')
        .select('*')
        .eq('partnership_id', partnership.id)
        .eq('club_id', clubId);

      if (statsError) throw statsError;

      // Filter by season if provided
      const relevantStats = seasonId 
        ? gameStats.filter(stat => stat.season_id === seasonId)
        : gameStats;

      if (relevantStats.length > 0) {
        // Calculate performance metrics for this context
        const wins = relevantStats.filter(stat => stat.result === 'win').length;
        const totalGames = relevantStats.length;
        const winRate = totalGames > 0 ? (wins / totalGames) * 100 : 0;
        const avgPerformanceVsExpected = relevantStats.length > 0
          ? relevantStats.reduce((sum, stat) => sum + parseFloat(stat.performance_vs_expected || 0), 0) / relevantStats.length
          : 0;

        // Determine partner name (the other player)
        const partner = partnership.player1_id === playerId 
          ? partnership.player2 
          : partnership.player1;

        enhancedPartnerships.push({
          ...partnership,
          partner,
          contextWins: wins,
          contextTotalGames: totalGames,
          contextWinRate: Math.round(winRate * 100) / 100,
          contextAvgPerformanceVsExpected: Math.round(avgPerformanceVsExpected * 1000) / 1000,
          chemistry_score: parseFloat(partnership.chemistry_score || 0) / 100, // Convert from percentage to decimal
          lastPlayedAt: relevantStats.length > 0 
            ? Math.max(...relevantStats.map(stat => new Date(stat.game_created_at).getTime()))
            : null
        });
      }
    }

    return enhancedPartnerships;
  } catch (error) {
    console.error('Error fetching top partnerships:', error);
    throw error;
  }
};

/**
 * Fetch partnership performance data over time
 * @param {string} playerId - The player's ID
 * @param {string} clubId - The club ID to filter by
 * @param {string} seasonId - Optional season ID to filter by
 * @param {string} partnershipId - Optional specific partnership ID
 * @returns {Promise<Array>} Array of performance data points over time
 */
export const fetchPartnershipPerformanceHistory = async (playerId, clubId, seasonId = null, partnershipId = null) => {
  try {
    // First, get partnerships for this player
    const { data: partnerships, error: partnershipsError } = await supabase
      .from('partnerships_with_stats')
      .select('*')
      .or(`player1_id.eq.${playerId},player2_id.eq.${playerId}`);

    if (partnershipsError) throw partnershipsError;

    if (!partnerships || partnerships.length === 0) {
      return [];
    }

    // Filter partnerships if specific partnership ID is provided
    const relevantPartnerships = partnershipId 
      ? partnerships.filter(p => p.id === partnershipId)
      : partnerships;

    if (relevantPartnerships.length === 0) {
      return [];
    }

    // Get partnership game stats for these partnerships
    const partnershipIds = relevantPartnerships.map(p => p.id);
    
    let query = supabase
      .from('partnership_game_stats')
      .select('*')
      .in('partnership_id', partnershipIds)
      .order('created_at', { ascending: true });

    const { data: gameStats, error } = await query;

    if (error) throw error;

    if (!gameStats || gameStats.length === 0) {
      return [];
    }

    // Filter by club and season if specified
    let filteredStats = gameStats;
    
    if (clubId) {
      filteredStats = filteredStats.filter(stat => 
        stat.club_id === clubId
      );
    }
    
    if (seasonId) {
      filteredStats = filteredStats.filter(stat => 
        stat.season_id === seasonId
      );
    }

    // Process the data to create performance history
    const performanceHistory = filteredStats.map(stat => {
      const partnership = relevantPartnerships.find(p => p.id === stat.partnership_id);
      
      if (!partnership) {
        return null;
      }
      
      // Determine partner ID
      const partnerId = partnership.player1_id === playerId 
        ? partnership.player2_id 
        : partnership.player1_id;

      return {
        date: stat.game_created_at,
        partnershipId: partnership.id,
        partnerId: partnerId,
        chemistryScore: parseFloat(partnership.chemistry_score || 0) / 100, // Convert from percentage to decimal
        result: stat.result,
        performanceVsExpected: parseFloat(stat.performance_vs_expected || 0),
        expectedWinProbability: parseFloat(stat.expected_win_probability || 0),
        preGameElo: stat.pre_game_elo,
        opponentElo: stat.opponent_elo
      };
    }).filter(Boolean); // Remove null entries

    return performanceHistory;
  } catch (error) {
    console.error('Error fetching partnership performance history:', error);
    throw error;
  }
};

/**
 * Get partner name for display
 * @param {Object} partnership - Partnership object
 * @param {string} currentPlayerId - Current player's ID
 * @returns {string} Partner's display name
 */
export const getPartnerDisplayName = (partnership, currentPlayerId) => {
  const partner = partnership.player1_id === currentPlayerId 
    ? partnership.player2 
    : partnership.player1;
  
  return partner ? `${partner.first_name} ${partner.last_name}` : 'Unknown Partner';
};

/**
 * Get partner gender for styling
 * @param {Object} partnership - Partnership object
 * @param {string} currentPlayerId - Current player's ID
 * @returns {string} Partner's gender
 */
export const getPartnerGender = (partnership, currentPlayerId) => {
  const partner = partnership.player1_id === currentPlayerId 
    ? partnership.player2 
    : partnership.player1;
  
  return partner?.gender || 'OTHER';
};

/**
 * Check if two players have a partnership
 * @param {string} player1Id - First player's ID
 * @param {string} player2Id - Second player's ID
 * @returns {Promise<boolean>} Whether the players have a partnership
 */
export const isPartnership = async (player1Id, player2Id) => {
  // If either player ID is undefined or null, they can't have a partnership
  if (!player1Id || !player2Id) {
    return false;
  }
  
  try {
    // Check if a partnership exists between these two players
    // We need to check both directions since partnerships can be stored either way
    const { data, error } = await supabase
      .from('partnerships_with_stats')
      .select('id')
      .or(`and(player1_id.eq.${player1Id},player2_id.eq.${player2Id}),and(player1_id.eq.${player2Id},player2_id.eq.${player1Id})`)
      .limit(1);
    
    if (error) {
      console.error('Error checking partnership:', error);
      return false;
    }
    
    return data && data.length > 0;
  } catch (error) {
    console.error('Error in isPartnership function:', error);
    return false;
  }
};

/**
 * Get all partnerships for a player
 * @param {string} playerId - The player's ID
 * @returns {Promise<Array>} Array of partnerships
 */
export const getPartnerships = async (playerId) => {
  // If playerId is undefined or null, return an empty array
  if (!playerId) {
    console.warn('getPartnerships called with invalid playerId:', playerId);
    return [];
  }
  
  try {
    const { data: partnerships, error } = await supabase
      .from('partnerships_with_stats')
      .select(`
        *,
        player1:player1_id(id, first_name, last_name, gender),
        player2:player2_id(id, first_name, last_name, gender)
      `)
      .or(`player1_id.eq.${playerId},player2_id.eq.${playerId}`);

    if (error) throw error;
    return partnerships || [];
  } catch (error) {
    console.error('Error fetching partnerships:', error);
    return [];
  }
};

/**
 * Update partnerships in the database
 * @param {Array} partnerships - Array of partnerships to update
 * @returns {Promise<void>}
 */
export const updatePartnerships = async (partnerships) => {
  if (!partnerships || partnerships.length === 0) {
    return;
  }
  
  try {
    // For each partnership, update or create it in the database
    for (const partnership of partnerships) {
      if (partnership.id) {
        // Update existing partnership (only core data, statistics are calculated automatically)
        const { error } = await supabase
          .from('partnerships')
          .update({
            pair_type: partnership.pair_type || 'levels',
            partnership_type: partnership.partnership_type || 'organic_partnership',
            updated_at: new Date().toISOString()
          })
          .eq('id', partnership.id);
          
        if (error) {
          console.error('Error updating partnership:', error);
        }
      } else if (partnership.player1Id && partnership.player2Id) {
        // Create new partnership (only core data, statistics are calculated automatically)
        const { error } = await supabase
          .from('partnerships')
          .insert([{
            player1_id: partnership.player1Id,
            player2_id: partnership.player2Id,
            club_id: partnership.club_id,
            season_id: partnership.season_id,
            pair_type: partnership.pair_type || 'levels',
            partnership_type: partnership.partnership_type || 'organic_partnership'
          }]);
          
        if (error) {
          // If it's a duplicate key error, we can ignore it
          if (error.code === '23505') {
            console.log('Partnership already exists, skipping creation');
          } else {
            console.error('Error creating partnership:', error);
          }
        }
      }
    }
  } catch (err) {
    console.error('Error in updatePartnerships:', err);
  }
};